/**
 * CookieConsent — DPDP-compliant cookie/analytics consent banner.
 * Posts decisions to /api/consent for audit log.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';

const STORAGE_KEY = 'snt_consent_v1';
const CONSENT_VERSION = 'v1.0.0';

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true, whatsapp: true });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setShow(true);
  }, []);

  async function save(consent) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now(), version: CONSENT_VERSION }));
    window.dispatchEvent(new CustomEvent('snt:consent', { detail: consent }));   // analytics.js applies it immediately
    setShow(false);
    // Log each consent decision (best-effort, fail-open)
    const types = ['terms', 'privacy', 'analytics', 'marketing', 'whatsapp'];
    for (const t of types) {
      const granted = t === 'terms' || t === 'privacy' ? true : consent[t];
      api.post('/api/consent', {
        consent_type: t, consent_version: CONSENT_VERSION, granted,
      }).catch(() => {});
    }
  }

  const acceptAll = () => save({ analytics: true, marketing: true, whatsapp: true });
  const acceptEssential = () => save({ analytics: false, marketing: false, whatsapp: false });
  const acceptCustom = () => save(prefs);

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: 'var(--bg-1, #131316)', borderTop: '2px solid var(--gold, #D4A574)',
      padding: 20, zIndex: 9999, boxShadow: '0 -10px 30px rgba(0,0,0,0.5)',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{
          fontFamily: 'Fraunces, serif', fontSize: 18, marginBottom: 8,
          color: 'var(--text, #F5F5F4)',
        }}>Your privacy matters.</div>
        <p style={{
          fontSize: 13, color: 'var(--text-2, #A8A8A4)',
          margin: '0 0 16px', maxWidth: 720, lineHeight: 1.55,
        }}>
          Under India's DPDP Act, we ask before using cookies for anything beyond keeping you logged in.
          You can change your choices any time in <a href="/privacy" style={{ color: 'var(--gold)' }}>Privacy Settings</a>.
        </p>

        {!showDetail ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={acceptAll} style={primaryBtn}>Accept all</button>
            <button onClick={acceptEssential} style={secondaryBtn}>Essential only</button>
            <button onClick={() => setShowDetail(true)} style={textBtn}>Customise</button>
          </div>
        ) : (
          <div>
            <ConsentRow label="Analytics" desc="Helps us understand what works and what doesn't."
              checked={prefs.analytics} onChange={(v) => setPrefs(p => ({ ...p, analytics: v }))} />
            <ConsentRow label="Marketing emails" desc="Personalised event recommendations + offers."
              checked={prefs.marketing} onChange={(v) => setPrefs(p => ({ ...p, marketing: v }))} />
            <ConsentRow label="WhatsApp notifications" desc="Booking confirmations + reminders via WhatsApp."
              checked={prefs.whatsapp} onChange={(v) => setPrefs(p => ({ ...p, whatsapp: v }))} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={acceptCustom} style={primaryBtn}>Save preferences</button>
              <button onClick={() => setShowDetail(false)} style={textBtn}>Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ label, desc, checked, onChange }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0',
      borderBottom: '1px solid var(--line)', cursor: 'pointer',
    }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 4, accentColor: 'var(--gold)' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{desc}</div>
      </div>
    </label>
  );
}

const primaryBtn = {
  padding: '10px 20px', background: 'var(--gold)', color: '#0A0A0B',
  border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600,
  letterSpacing: '0.03em', cursor: 'pointer',
};
const secondaryBtn = {
  padding: '10px 20px', background: 'transparent',
  color: 'var(--text)', border: '1px solid var(--line-strong, rgba(255,255,255,0.12))',
  borderRadius: 4, fontSize: 13, cursor: 'pointer',
};
const textBtn = {
  padding: '10px 12px', background: 'transparent', color: 'var(--text-2)',
  border: 'none', fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
};
